import React, { useContext, useEffect, useState } from 'react';
import { DataContext } from '../context/DataContext';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box } from '@mui/material';
import TradingMetricsCalculator from '../helpers/TradingMetricsCalculator';
import { TradingData } from '../helpers/CSVParser';
import { YearlyTradingMetrics } from '../interfaces/Metrics';
import { SettingsContext } from '../context/SettingsContext';
import { LoadingContext } from '../context/LoadingContext';
import { useTranslation } from '../context/TranslationContext';

interface YearlyMetricEntry {
    title: string;
    metrics: YearlyTradingMetrics[];
}

const Annual = () => {
    const { dataset, getTotalInitialCapital } = useContext(DataContext);
    const { darkMode, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
    const { setLoading } = useContext(LoadingContext);
    const { t } = useTranslation();
    const [metricsWithTitle, setMetricsWithTitle] = useState<YearlyMetricEntry[]>([]);

    const getCellStyle = (value: number) => ({
        color: value >= 0 ? (darkMode ? '#089981' : 'green') : (darkMode ? '#FF6347' : 'red')
    });

    const formatValue = (value: number, decimals: number) => {
        return value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    // const formatDate = (date: Date) => {
    //     const options = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };
    //     return date.toLocaleDateString(getLocale(), options);
    // };

    useEffect(() => {
        setLoading(true);
        setTimeout(() => { // Simulate a delay for the loading spinner
            const filesToConsider = dataset.files.filter(file => file.include === true);
            if (filesToConsider.length === 0) {
                setMetricsWithTitle([]);
                setLoading(false);
                return;
            }
            let initialCapital = 0;
            let allTrades: TradingData[] = [];
            filesToConsider.forEach(file => {
                initialCapital += file.initialCapital;
                // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                let filteredData = file.parsedData.filter(trade => trade.type.toLowerCase().includes('exit'));;
                if (dataset.useDeepBacktest && dataset.customStartDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                    });
                }
                if (dataset.useDeepBacktest && dataset.customEndDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                    });
                }
                allTrades = [...allTrades, ...filteredData];
            });

            const allMetrics: YearlyMetricEntry[] = [];
            const accumulatedMetrics = new TradingMetricsCalculator(allTrades, true, initialCapital).calculateYearlyMetrics();
            allMetrics.push({ title: 'All', metrics: accumulatedMetrics });
            
            dataset.files.filter(file => file.include === true).forEach(file => {
                // filter parsedData by customStartDate and customEndDate if useAllTrades is false
                let filteredData = file.parsedData.filter(trade => trade.type.toLowerCase().includes('exit'));;
                if (dataset.useDeepBacktest && dataset.customStartDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customStartDate ? tradeDate >= new Date(dataset.customStartDate) : false;
                    });
                }
                if (dataset.useDeepBacktest && dataset.customEndDate) {
                    filteredData = filteredData.filter(trade => {
                        const tradeDate = new Date(trade.datetime);
                        return dataset.customEndDate ? tradeDate <= new Date(dataset.customEndDate) : false;
                    });
                }
                const metrics = new TradingMetricsCalculator(filteredData, true, file.initialCapital).calculateYearlyMetrics();
                if (metrics) {
                    allMetrics.push({ title: file.customId, metrics });
                }
            });

            setMetricsWithTitle(allMetrics);
            setLoading(false);
        }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataset]);
    
    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                {t("Yearly Metrics")}
            </Typography>

            {metricsWithTitle.map((metricsWithTitle, index) => (
                <Box key={index} mb={3}>
                    <Paper elevation={3} style={{ padding: '16px', background: 'linear-gradient(to bottom,rgba(224, 247, 233, 0.03),rgba(26, 83, 48, 0.05))' }}>
                        <Typography variant="h6" gutterBottom>
                            <strong>{metricsWithTitle.title}</strong>
                        </Typography>
                        {index === 0 && <Typography>{t("Total initial capital")}: {formatValue(getTotalInitialCapital(), 2)} {showCurrencySymbol && getCurrencySymbol()}</Typography>}
                        {index !== 0 && dataset.files.filter(file => file.include).map((file, index2) => {
                            if (index-1 === index2 ) {
                                return (
                                    <Typography key={file.customId}>
                                    {t("Initial capital")} {file.customId}: {formatValue(file.initialCapital, 2)} {showCurrencySymbol && getCurrencySymbol()}
                                    </Typography>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </Paper>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><Typography><strong>{t("Year")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Net Profit")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Return on Initial Equity")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Return on Total Equity")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Trades")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Profit Factor")}</strong></Typography></TableCell>
                                    <TableCell align='right'><Typography><strong>{t("Percent Profitable Trades")}</strong></Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {metricsWithTitle.metrics.map((entry, index) => (
                                    <TableRow key={index}>
                                        <TableCell><Typography><strong>{entry.period}</strong></Typography></TableCell>
                                        <TableCell align='right' style={getCellStyle(entry.netProfit)}><Typography>{formatValue(entry.netProfit, 2)} {showCurrencySymbol && getCurrencySymbol()}</Typography></TableCell>
                                        <TableCell align='right' style={getCellStyle(entry.returnOnIE)}><Typography>{formatValue(entry.returnOnIE, 2)} {showCurrencySymbol && getCurrencySymbol()}</Typography></TableCell>
                                        <TableCell align='right' style={getCellStyle(entry.returnOnTE)}><Typography>{formatValue(entry.returnOnTE, 2)} {showCurrencySymbol && getCurrencySymbol()}</Typography></TableCell>
                                        <TableCell align='right'><Typography>{formatValue(entry.trades, 0)}</Typography></TableCell>
                                        <TableCell align='right'><Typography>{formatValue(entry.profitFactor, 2)}</Typography></TableCell>
                                        <TableCell align='right'><Typography>{formatValue(entry.percentProfitable, 2)} %</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ))}
        </Box>
    );
};

export default Annual;
